/**
 * @file Utility functions for generating user initials from names
 */

/**
 * Generate initials from a name string.
 *
 * Extracts the first letter of each word in the name and returns
 * up to 2 characters in uppercase.
 *
 * @param name - Full name to generate initials from
 * @returns Uppercase initials (max 2 characters) or null if name is empty
 *
 * @example
 * ```ts
 * getInitials("John Doe")           // "JD"
 * getInitials("Jane")               // "J"
 * getInitials("Mary Jane Watson")   // "MJ"
 * getInitials("")                   // null
 * getInitials("  ")                 // null
 * ```
 */
export function getInitials(name: string | null | undefined): string | null {
  // Handle null/undefined
  if (!name) {
    return null;
  }

  // Trim and check for empty string
  const trimmedName = name.trim();

  if (!trimmedName) {
    return null;
  }

  // Split by whitespace, map to first character, join, and limit to 2 chars
  return trimmedName
    .split(/\s+/) // Split on any whitespace (handles multiple spaces)
    .map((word) => word[0]) // Get first character of each word
    .join("") // Join all first characters
    .substring(0, 2) // Limit to 2 characters
    .toUpperCase(); // Convert to uppercase
}
